import React from 'react';
import gql from 'graphql-tag';

import { InputDlg } from './input-dialog';
import { Form as FForm, Formik, useField } from 'formik';
import {
  ButtonSearch,
  FieldInput,
  FormCol6,
  FilterProps,
  promiseModalSelect,
  FormRow,
  TableCellV8,
  FFProps,
  FormFieldModalDlg,
  FormCol,
  CellDateV8,
} from 'motion-components';
import { Form, Col } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';
import { getRouteByName } from 'app/routes';
import { isFotokiklosi, isRebattery } from 'shared-pages/common/constants';
import { FieldSelectNomos } from '../field-select/fs-nomos';

export const FormFieldCollectionDlg = ({
  as = 'div',
  label = 'Συλλογή',
  idFieldName = 'collectionId',
  nameFieldName = 'collectionCode',
  disabled,
  placeholder = '',
  required = false,
  size,
  onChange,
}: FFProps) => {
  const [field, { value }] = useField(idFieldName);
  const path = generatePath(getRouteByName('Collection'), { id: value ?? 0 });
  const showLabel = value ? <Link to={path}>{label}</Link> : label;

  return (
    <FormFieldModalDlg
      DlgComponent={FieldCollectionDlg}
      as={as}
      label={showLabel}
      idFieldName={idFieldName}
      nameFieldName={nameFieldName}
      disabled={disabled}
      onChange={onChange}
      required={required}
    />
  );
};

/* ------------------------------------------
   Select Collections with Modal Search
   ------------------------------------------ */
type Props = {
  initValueId?: string;
  initValueName?: string;
  onValueChange: (valueId: string, valueName: string) => void;
  required?: boolean;
  initFilters?: {} | (() => {});
  disabled?: boolean;
};

export const FieldCollectionDlg = ({
  initValueId = undefined,
  initValueName = '',
  onValueChange,
  required = false,
  initFilters = {},
  disabled = false,
}: Props) => {
  const { appStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const valueChanged = (valueId, valueName) => {
    // επιστρέφει valueId=uknown όταν πατηθεί το Cancel
    if (valueId) onValueChange(valueId, valueName);
  };

  return (
    <InputDlg
      valueId={initValueId}
      valueName={initValueName}
      disable={disabled}
      onDlgClick={async (event) => {
        const filters = typeof initFilters === 'function' ? initFilters() : initFilters;
        const autoFetchData = !!filters?.cstGuid;
        const res = await promiseModalSelectCollection({
          initFilters: filters,
          appStore,
          autoFetchData,
          t,
        });
        return { newId: res?.id, newName: res?.code };
      }}
      onValueChange={valueChanged}
    />
  );
};

// -------------------------------------------------------------------------------
// Modal Select Collection
// -------------------------------------------------------------------------------

const gqlGetQuery = gql`
  query getCollections($limit: Int, $offset: Int, $order: String, $where: String) {
    getCollections(limit: $limit, offset: $offset, order: $order, where: $where) {
      data {
        id
        code
        seriesId
        codePrefix
        codeNumber
        dateCollected
        collectionPointId
        collectorId
        wastereceptionpointId
        legacyAppId
        recordDate
        isActive
        firstDate
        lastDate
        finDispNote
        firstUser
        lastUser
        collectionpoint {
          id
          name
          addressLine1
          addressLine2
          addressArea
          addressCity
          addressDistrict
          phone
        }
        collector {
          id
          name
        }
        wastereceptionpoint {
          id
          name
        }
        collectionLines {
          id
          aa
          materialId
          finDispNote
          qty
          netWeight
          material {
            id
            name
          }
        }
      }
      recordCount
    }
  }
`;

// -------------------------------------------------------------------------------
// Filters
// -------------------------------------------------------------------------------
const buildWhereFromFilters = (values, appStore) => {
  // Φτιάξε το where για το graphql
  let where = {};
  if (values) {
    if (values.searchText) {
      const searchText = '%' + values.searchText + '%';
      const likeObj = { $like: searchText };
      where = {
        ...where,
        $or: [{ code: likeObj }],
      };
    }
  }
  return where;
};

const Filters: (props: FilterProps) => JSX.Element = ({ onChange, initialValues }) => {
  const { appStore } = useRootStore();
  return (
    <Formik initialValues={initialValues ? initialValues : { searchText: '' }} onSubmit={onChange}>
      <Form as={FForm}>
        <FormRow className="mb-3 align-items-center">
          <FieldInput
            as={FormCol6}
            name="searchText"
            id="searchText"
            label={'Κωδικός Συλλογής'}
            type="search"
          />
          <Col className="pl-0 pr-0 me-3 ">
            <ButtonSearch size="sm" type="submit" className="float-right mb-2" />
          </Col>
        </FormRow>
      </Form>
    </Formik>
  );
};

// -------------------------------------------------------------------------------

export const promiseModalSelectCollection = (props) => {
  const t = props.t;
  const columns = [
    {
      id: 'id',
      header: t('Id'),
      columnName: t('Id'),
      accessorKey: 'id',
      orderByField: 'id',
      cell: TableCellV8,
      size: 20,
    },
    {
      id: 'code',
      header: t('Κωδικός Εντύπου'),
      columnName: t('Κωδικός Εντύπου'),
      accessorKey: 'code',
      orderByField: 'code',
      cell: TableCellV8,
      size: 20,
    },
    {
      id: 'orderedDate',
      header: t('Ημερομηνία Συλλογής'),
      columnName: t('Ημερομηνία Συλλογής'),
      accessorKey: 'dateCollected',
      orderByField: 'dateCollected',
      cell: CellDateV8,
      size: 50,
      meta: { type: 'text' },
    },
    {
      id: 'collectionPointId',
      header: t('Σημείο Συλλογής'),
      columnName: t('Σημείο Συλλογής'),
      accessorKey: 'collectionpoint.name',
      orderByField: 'collectionpoint.name',
      cell: ({ getValue, row }) => (
        <div className="text-left">
          <div>{row.original.collectionpoint.name}</div>
          <div style={{ color: 'gray', fontSize: 'smaller' }}>
            {row.original.collectionpoint.addressLine1},&nbsp;
            {row.original.collectionpoint.addressArea},&nbsp;
            {row.original.collectionpoint.addressCity},&nbsp;
            {row.original.collectionpoint.addressDistrict}
          </div>
        </div>
      ),
      size: 35,
      meta: { type: 'text' },
    },

    {
      id: 'collectorId',
      header: t('Συλλέκτης'),
      columnName: t('Συλλέκτης'),
      accessorKey: 'collector.name',
      orderByField: 'collector.name',
      cell: TableCellV8,
      size: 35,
      meta: { type: 'text' },
    },
    {
      id: 'wastereceptionpointId',
      header: t('Προορισμός'),
      columnName: t('Προορισμός'),
      accessorKey: 'wastereceptionpoint.name',
      orderByField: 'wastereceptionpoint.name',
      cell: TableCellV8,
      size: 35,
      meta: { type: 'text' },
    },
    {
      id: 'collectionLines',
      header: t('Απόβλητα'),
      columnName: t('Απόβλητα'),
      accessorKey: 'collectionLines',
      orderByField: 'collectionLines',
      cell: ({ getValue, row }) => {
        function compare(a, b) {
          if (a.aa < b.aa) {
            return -1;
          }
          if (a.aa > b.aa) {
            return 1;
          }
          return 0;
        }
        const lines = row.original.collectionLines;
        return (
          <div className="text-left">
            {lines.sort(compare).map((v, i) => {
              return (
                <div key={i + v.aa}>
                  {v?.aa}: {v?.material?.name} ({v?.qty} ΤΕΜ) {v?.netWeight} kg
                </div>
              );
            })}
          </div>
        );
      },
      size: 35,
    },
  ];
  return promiseModalSelect({
    ...props,
    title: 'Επιλογή Συλλογής',
    columns,
    gqlQuery: gqlGetQuery,
    buildWhereFromFilters: (values) => buildWhereFromFilters(values, props.appStore),
    initFilters: props.initFilters,
    Filters: Filters,
    orderBy: 'code',
    size: 'xl',
  });
};
// -------------------------------------------------------------------------------
